import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { CommentCreate, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { CrudStatus } from '@wdx/shared/utils';
import { Observable, filter, switchMap, Subject, tap } from 'rxjs';
import * as chatActions from './chat.actions';
import { Chat } from './chat.reducer';
import * as chatSelectors from './chat.selectors';
import { selectChat } from './chat.selectors';

export interface CommentEvent {
    entityType: SystemEntity;
    entityId: string;
    source?: string;
    eventType: 'create' | 'resolveIssue';
}

@Injectable({ providedIn: 'root' })
export class ChatFacade {
    // not using the store for this as it is already too noisy when debugging with existing events
    // being sent through it (we should perhaps move all system events to a non-ngrx service to fix this)
    private commentEvents = new Subject<CommentEvent>();
    public commentEvents$ = this.commentEvents.asObservable();

    private instanceId;

    constructor(private store$: Store) {
        this.instanceId = Date.now().toString();
        console.log(this.instanceId, 'ChatFacade created');
    }

    getChat$(entityType: SystemEntity, entityId: string): Observable<Chat> {
        return this.store$.pipe(
            select(selectChat({ entityId, entityType })),
            switchMap((chat) => {
                if (!chat) {
                    this.store$.dispatch(
                        chatActions.loadChat({
                            entityId,
                            entityType,
                        }),
                    );
                }
                return this.store$.pipe(
                    select(
                        chatSelectors.selectChat({
                            entityId,
                            entityType,
                        }),
                    ),
                    filter(Boolean),
                );
            }),
        );
    }

    allChatsOfTypeUpdated$(entityType: SystemEntity): Observable<Chat[]> {
        return this.store$.pipe(
            select(chatSelectors.selectChatsOfType({ entityType })),
            filter(
                (chats) =>
                    !!chats.length &&
                    chats.every((chat) => chat.status === CrudStatus.Success),
            ),
        );
    }

    createComment(
        entityType: SystemEntity,
        entityId: string,
        commentCreate: CommentCreate,
        source?: string,
    ) {
        this.triggerCreateCommentEvent(entityType, entityId, source);
        this.store$.dispatch(
            chatActions.createComment({
                entityId,
                entityType,
                commentCreate,
            }),
        );
    }

    triggerCreateCommentEvent(
        entityType: SystemEntity,
        entityId: string,
        source?: string,
    ) {
        this.commentEvents.next({
            entityType,
            entityId,
            source,
            eventType: 'create',
        });
    }
}
